import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { AuthSession, CompanyMembership, Role } from '../api/types'
import { fetchApi, isRealBackend, setStoredToken } from '../api/fetchClient'

const STORAGE_KEY = 'adaptivo.szamla.session'
const COMPANY_STORAGE_KEY = 'adaptivo.szamla.activeCompany'

interface AuthContextValue {
  session: AuthSession | null
  isAuthenticated: boolean
  activeCompanyId: string | null
  activeMembership: CompanyMembership | null
  activeRole: Role | null
  setActiveCompany: (companyId: string) => void
  login: (session: AuthSession) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => loadSession())
  const [activeCompanyId, setActiveCompanyIdState] = useState<string | null>(() => loadActiveCompany())

  useEffect(() => {
    if (session) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    } else {
      window.localStorage.removeItem(STORAGE_KEY)
    }
  }, [session])

  useEffect(() => {
    if (activeCompanyId) {
      window.localStorage.setItem(COMPANY_STORAGE_KEY, activeCompanyId)
    } else {
      window.localStorage.removeItem(COMPANY_STORAGE_KEY)
    }
  }, [activeCompanyId])

  const login = useCallback((s: AuthSession) => {
    setSession(s)
    const first = s.memberships[0]
    if (first) setActiveCompanyIdState(first.companyId)
  }, [])

  const logout = useCallback(() => {
    if (isRealBackend) {
      // Server-side session revoke — kifelé fire-and-forget, ha a token már lejárt, nem baj.
      void fetchApi.logout().catch(() => {
        // noop
      })
    } else {
      setStoredToken(null)
    }
    setSession(null)
    setActiveCompanyIdState(null)
  }, [])

  const setActiveCompany = useCallback(
    (companyId: string) => {
      if (!session) return
      const isMember = session.memberships.some((m) => m.companyId === companyId)
      if (isMember) setActiveCompanyIdState(companyId)
    },
    [session],
  )

  const value = useMemo<AuthContextValue>(() => {
    const activeMembership =
      session?.memberships.find((m) => m.companyId === activeCompanyId) ?? null
    return {
      session,
      isAuthenticated: !!session,
      activeCompanyId,
      activeMembership,
      activeRole: activeMembership?.role ?? null,
      setActiveCompany,
      login,
      logout,
    }
  }, [session, activeCompanyId, setActiveCompany, login, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth: AuthProvider hiányzik a fa felett')
  return ctx
}

function loadSession(): AuthSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AuthSession
    if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() < Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function loadActiveCompany(): string | null {
  try {
    return window.localStorage.getItem(COMPANY_STORAGE_KEY)
  } catch {
    return null
  }
}
